import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { getCities } from "../../store/actions";
import { fireAnalytics } from "../../utils/firebase.js";
import { Building2, UserCheck, ArrowLeft, ArrowRight } from "lucide-react";
import { getSessionId, getDeviceCategory } from "../../utils/analyticsHelpers.ts";
import WorkZoneSelectorNew from "../../components/UI/WorkZoneSelectorNew/WorkZoneSelectorNew.tsx";
import styles from "./WorkZonesNew.module.scss";
import LogoEdify from "../../assets/images/Logo-edify-no-bg.png";
import RegisterStepsNew from "../../components/UI/RegisterStepsNew/RegisterStepsNew.tsx";

interface WorkZonesNewProps {
  personaForm: any;
  empresaForm: any;
  userSkills: any[];
  getCities: (jurisdictionId: string) => void;
  cities: any[];
}

interface WorkZone {
  jurisdiccion: { id: string; name: string };
  name: string;
  city: string;
}

const WorkZonesNew: React.FC<WorkZonesNewProps> = (props) => {
  const [workZoneType, setWorkZoneType] = useState<'allCountry' | 'specificZones'>('allCountry');
  const [jobBoardZones, setJobBoardZones] = useState<WorkZone[]>([]);
  const navigate = useNavigate();

  React.useEffect(() => {
    // Mantener evento antiguo durante transición
    fireAnalytics("work_zones_step", { category: "registration" });
    
    // Nuevo evento estandarizado
    fireAnalytics("onboarding_work_zones_view", {
      category: "onboarding",
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      action: "work_zones_view",
      account_type: accountType || ""
    });
    
    const storedWorkZones = localStorage.getItem("workZonesNew");
    const storedAllCountry = localStorage.getItem("allCountryNew");
    
    if (storedWorkZones) {
      try {
        const parsedWorkZones = JSON.parse(storedWorkZones);
        setJobBoardZones(parsedWorkZones);
      } catch (error) {
        console.error("Error parsing stored work zones:", error);
      }
    }
    
    if (storedAllCountry) {
      try {
        const parsedAllCountry = JSON.parse(storedAllCountry);
        setWorkZoneType(parsedAllCountry ? 'allCountry' : 'specificZones');
      } catch (error) {
        console.error("Error parsing stored all country setting:", error);
      }
    }
  }, []);

  const getAccountType = () => {
    if (props.personaForm && Object.keys(props.personaForm).length > 0) {
      return "persona";
    } else if (props.empresaForm && Object.keys(props.empresaForm).length > 0) {
      return "empresa";
    }
    return "persona";
  };

  const accountType = getAccountType();

  const canContinue = () => {
    return workZoneType === 'allCountry' || jobBoardZones.length > 0;
  };

  const handleBack = () => {
    navigate("/register-form");
  };

  const handleNext = () => {
    localStorage.setItem("workZonesNew", JSON.stringify(jobBoardZones));
    localStorage.setItem("allCountryNew", JSON.stringify(workZoneType === 'allCountry'));
    
    navigate("/resumen");
  };

  const handleSkip = () => {
    setWorkZoneType('allCountry');
    
    const defaultWorkZones = [];

    localStorage.setItem("workZonesNew", JSON.stringify(defaultWorkZones));
    localStorage.setItem("allCountryNew", JSON.stringify(true)); 

    navigate("/resumen");
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <motion.img
            src={LogoEdify}
            alt="Edify Logo"
            className={styles.logo}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          />

          <RegisterStepsNew stepCount={3} />

          <motion.h1
            className={styles.mainTitle}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {accountType === "persona" ? "¿Dónde trabajas?" : "¿Dónde opera tu empresa?"}
          </motion.h1>


        </div>

        <div className={styles.formContainer}>
          <motion.div
            className={styles.workZonesSection}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {/* Usar el WorkZoneSelectorNew original */}
            <WorkZoneSelectorNew
              workZoneType={workZoneType}
              onWorkZoneTypeChange={setWorkZoneType}
              jobBoardZones={jobBoardZones}
              onJobBoardZonesChange={setJobBoardZones}
              getCities={props.getCities}
              cities={props.cities}
            />
          </motion.div>

          <motion.div
            className={styles.navigationButtons}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
          >
            <button
              type="button"
              className={styles.backButton}
              onClick={handleBack}
            >
              <ArrowLeft size={16} />
              Atrás
            </button>

            <div className={styles.rightButtons}>
              <button
                type="button"
                className={styles.skipButton}
                onClick={handleSkip}
              >
                Omitir
              </button>

              <button
                type="button"
                className={styles.nextButton}
                onClick={handleNext}
                disabled={!canContinue()}
              >
                Continuar
                <ArrowRight size={16} />
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    personaForm: state.register.personaForm,
    empresaForm: state.register.empresaForm,
    userSkills: state.register.userSkills,
    cities: state.register.cities,
  };
};

export default connect(mapStateToProps, { getCities })(WorkZonesNew);
